import { useEffect, useMemo, ChangeEvent } from 'react';
import { Checkbox, TableCell, TableRow } from '@mui/material';
import { InvoiceService } from 'api';
import { LoaderFullScreen } from 'components/Loaders/LoaderFullScreen';
import { StyledTableCell } from 'components/Table';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  numberWithCommas,
  currencyStringToNumber,
} from 'utils/numberFunctions.utils';
import { HeadCell, TableData } from '../../invoicesTable.types';

type Props = {
  selectedInvoices: TableData[];
  headerCells: HeadCell[];
  numSelected: number;
  rowCount: number;
  onSelectAllClick: (event: ChangeEvent<HTMLInputElement>) => void;
};

export const InvoicesTableSummary = ({
  selectedInvoices,
  headerCells,
  numSelected,
  rowCount,
  onSelectAllClick,
}: Props) => {
  const { t } = useTranslation(['invoice']);
  const trailingKsefColumnCount = headerCells.filter(
    (cell) => cell.id === 'ksefState' || cell.id === 'ksefActions',
  ).length;
  const summaryColumnCount = headerCells.length - trailingKsefColumnCount;

  const { listSummaryData, loading, getListSummary } =
    InvoiceService.useGetListSummary();

  const summedCurrencies = useMemo(() => {
    const groupedByCurrency = _.groupBy(selectedInvoices, 'currency');
    return _.mapValues(groupedByCurrency, (g) =>
      g.reduce(
        (p, c) => p + currencyStringToNumber(c.grossValue, c.currency),
        0,
      ),
    );
  }, [selectedInvoices]);

  useEffect(() => {
    selectedInvoices.length &&
      getListSummary(selectedInvoices.map((i) => i.id));
  }, [selectedInvoices, getListSummary]);

  return (
    <>
      <LoaderFullScreen loading={loading} />
      <TableRow>
        <TableCell colSpan={summaryColumnCount - 1} padding='checkbox'>
          <Checkbox
            color='primary'
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              'aria-label': 'Select all invoices',
            }}
          />
        </TableCell>
        <StyledTableCell align='right' height='condensed' fontWeight={600}>
          Total:
        </StyledTableCell>
        <StyledTableCell align='right' height='condensed' fontWeight={600}>
          {listSummaryData
            ? selectedInvoices.length
              ? numberWithCommas(listSummaryData)
              : '0'
            : '0'}{' '}
          PLN
        </StyledTableCell>
        {trailingKsefColumnCount > 0 && (
          <StyledTableCell
            colSpan={trailingKsefColumnCount}
            height='condensed'
          />
        )}
      </TableRow>
      {Object.keys(summedCurrencies).map((c, idx) => (
        <TableRow key={c}>
          <StyledTableCell
            colSpan={idx ? summaryColumnCount : summaryColumnCount - 1}
            height='condensed'
          />
          {!idx && (
            <StyledTableCell align='right' height='condensed' fontWeight={600}>
              {t('allocationSummaryCount', { ns: 'invoice' })}
            </StyledTableCell>
          )}
          <StyledTableCell align='right' height='condensed' fontWeight={600}>
            {numberWithCommas(summedCurrencies[c]) + ' ' + c}
          </StyledTableCell>
          {trailingKsefColumnCount > 0 && (
            <StyledTableCell
              colSpan={trailingKsefColumnCount}
              height='condensed'
            />
          )}
        </TableRow>
      ))}
    </>
  );
};
